import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { RowSelectionState } from '@tanstack/react-table';

// Components
import TanStackGrid from './TanStackGrid';
import ErrorMessage from '../UI/ErrorMessage';
import ActiveFiltersBar from '../UI/ActiveFiltersBar';
import Alert from '../../global/Alert/Alert';
import PopupMessage from './components/PopupMessage';
import SaveFiltersDialog from './components/SaveFiltersDialog';

// Hooks
import { useGridState } from './hooks/useGridState';
import { useFilterManagement } from './hooks/useFilterManagement';
import { useDataFetching } from './hooks/useDataFetching';
import { usePaginationAndSorting } from './hooks/usePaginationAndSorting';
import { useColumnManagement } from './hooks/useColumnManagement';
import { useFilterSaving } from './hooks/useFilterSaving';
import { useSelectionHandling } from './hooks/useSelectionHandling';

// Context and Utils
import { userAppContext } from '../../context/Context';
import { initializeFiltersFromURL, shouldShowRestrictionError } from './utils/index';
import { isSourceEnabledForTab } from '../../modules/search/searchGrid/helpers/sourceListHelpers';

// Types and Constants
import { GridProps } from './types/index';
import { SHOW_SUMMARIZE, ERROR_MESSAGES } from './constants/index';

const Grid: React.FC<GridProps> = ({
  columns,
  columnVisibilityModel,
  columnOrder,
  setColumnOrder,
  view,
  source,
  tab,
  panelTab,
  searchItems,
  isEvidence,
  isMapping,
  setTotalResults,
  getFilterValueRef,
  onData,
  sourceWeights,
  enableEnrichmentSelection = false,
  getRowId,
  getRowIdForEnrichment,
  enrichmentGetSelectionRef,
  ...rest
}) => {
  const [enrichmentRowSelection, setEnrichmentRowSelection] = useState<RowSelectionState>({});
  const enrichmentSelectedRowsMapRef = useRef<Record<string, any>>({});
  const enrichmentSelectedIdsRef = useRef<string[]>([]);
  const rowDataRef = useRef<any[]>([]);
  const navigate = useNavigate();
  const userContext = userAppContext();
  const {
    isAuthenticated,
    groups,
    restrictGridDisplay,
    restrictBrowseAll,
    restrictSources,
    restrictChemical,
    mappingContent,
    user,
  } = userContext;

  // Initialize state with custom hooks
  const initialFilters = useMemo(() => initializeFiltersFromURL(rest.defaultFilterValue), [rest.defaultFilterValue]);
  const gridState = useGridState(initialFilters);

  // Extract state and refs from gridState
  const {
    rowData,
    setRowData,
    totalCount,
    setTotalCount,
    loading,
    setLoading,
    showPopup,
    setShowPopup,
    skip,
    setSkip,
    limit,
    setLimit,
    orderByParam,
    setOrderByParam,
    order,
    setOrder,
    showFiltersModal,
    setShowFiltersModal,
    showCopiedAlert,
    setShowCopiedAlert,
    pendingFiltersToSave,
    setPendingFiltersToSave,
    alertMessage,
    setAlertMessage,
    filtersLoadedFromURL,
    setFiltersLoadedFromURL,
    isSessionExpired,
    setIsSessionExpired,
    defaultFilterValue,
    setDefaultFilterValue,
    cacheRef,
    gridContainerRef,
    prevDataRef,
    fetchTimeoutRef,
    prevTabRef,
  } = gridState;

  rowDataRef.current = rowData ?? [];

  // Initialize hooks
  const filterManagement = useFilterManagement(
    defaultFilterValue,
    setDefaultFilterValue,
    isEvidence,
    filtersLoadedFromURL,
    setFiltersLoadedFromURL,
    gridContainerRef,
    gridState.setURLWithFilters,
    setPendingFiltersToSave,
    setSkip
  );

  useDataFetching({
    view,
    source,
    tab,
    searchItems,
    isEvidence,
    isMapping,
    skip,
    limit,
    orderByParam,
    order,
    defaultFilterValue,
    sourceWeights,
    userContext,
    mappingContent,
    checkIncompleteFilters: filterManagement.checkIncompleteFilters,
    setRowData,
    setTotalCount,
    setTotalResults,
    setLoading,
    setIsSessionExpired,
    setShowPopup,
    setIncompleteFilterMessage: () => {},
    cacheRef,
    prevDataRef,
    fetchTimeoutRef,
  });

  const isTrial = groups?.includes('ROLE_TRIAL');
  const paginationAndSorting = usePaginationAndSorting({
    isAuthenticated,
    isTrial,
    restrictBrowseAll,
    limit,
    orderByParam,
    order,
    setSkip,
    setLimit,
    setOrderByParam,
    setOrder,
  });

  const columnManagement = useColumnManagement({ columns, tab });

  const filterSaving = useFilterSaving({
    pendingFiltersToSave,
    isEvidence,
    tab,
    view,
    source,
    searchItems,
    user,
    setAlertMessage,
    setShowCopiedAlert,
    setShowFiltersModal,
  });

  const selectionHandling = useSelectionHandling({ onData, setSelectedRow: () => {} });

  const onDataRef = useRef(onData);
  onDataRef.current = onData;

  const resolveRowForEnrichmentId = (id: string, data: any[] | undefined, prev: Record<string, any>) => {
    const byExact = data?.find((r: any, i: number) => getRowId?.(r, i) === id);
    if (byExact !== undefined) return byExact;
    if (prev[id] !== undefined) return prev[id];
    const logicalId = id.replace(/-\d+$/, '');
    return data?.find((r: any) => getRowIdForEnrichment?.(r) === logicalId);
  };

  useEffect(() => {
    if (!enrichmentGetSelectionRef) return;
    const selectedIds = Object.keys(enrichmentRowSelection).filter(k => enrichmentRowSelection[k]);
    enrichmentSelectedIdsRef.current = selectedIds;
    enrichmentGetSelectionRef.current = () => ({
      selectedIds: enrichmentSelectedIdsRef.current,
      rowData: rowDataRef.current ?? [],
      getRowId: getRowId ?? null,
      tab: panelTab ?? tab,
    });
  }, [enrichmentGetSelectionRef, rowData, getRowId, tab, panelTab, enrichmentRowSelection]);

  useEffect(() => {
    if (!enableEnrichmentSelection || !getRowId) return;
    const selectedIds = Object.keys(enrichmentRowSelection).filter(k => enrichmentRowSelection[k]);
    const selectedCount = selectedIds.length;
    const prevMap = enrichmentSelectedRowsMapRef.current;
    const nextMap: Record<string, any> = {};
    for (const id of selectedIds) {
      const row = resolveRowForEnrichmentId(id, rowData, prevMap);
      if (row !== undefined) nextMap[id] = row;
    }
    enrichmentSelectedRowsMapRef.current = nextMap;
    const selectedRows = selectedIds.map((id: string) => nextMap[id]).filter(Boolean);
    const cb = onDataRef.current;
    if (cb) cb(selectedRows.length > 0, selectedRows, selectedCount, selectedIds);
  }, [enableEnrichmentSelection, enrichmentRowSelection, rowData, getRowId, getRowIdForEnrichment]);

  useEffect(() => {
    if (!enableEnrichmentSelection) setEnrichmentRowSelection({});
  }, [enableEnrichmentSelection]);

  const prevTabEnrichmentRef = useRef<string>(tab);
  useEffect(() => {
    if (prevTabEnrichmentRef.current !== tab) {
      prevTabEnrichmentRef.current = tab;
      setEnrichmentRowSelection({});
      enrichmentSelectedRowsMapRef.current = {};
    }
  }, [tab]);

  // Memoized values for TanStackGrid
  const memoizedFilterValues = useMemo(
    () =>
      defaultFilterValue.reduce((acc: Record<string, string>, filter) => {
        if (filter.value) {
          acc[filter.name] = filter.value;
        }
        return acc;
      }, {}),
    [defaultFilterValue]
  );

  const memoizedFilterOperators = useMemo(
    () =>
      defaultFilterValue.reduce((acc: Record<string, string>, filter) => {
        if (filter.operator) {
          acc[filter.name] = filter.operator;
        }
        return acc;
      }, {}),
    [defaultFilterValue]
  );

  // Handle tab changes (user switched tab) – do not clear when loading a saved search (URL has 'f')
  useEffect(() => {
    if (prevTabRef.current !== tab) {
      const hasFilterInUrl = new URLSearchParams(window.location.search).get('f');
      const isLoadingFromSavedSearch = hasFilterInUrl && !filtersLoadedFromURL;
      if (!isLoadingFromSavedSearch) {
        filterManagement.handleClearAllFilters();
      }
      setFiltersLoadedFromURL(false);
      prevTabRef.current = tab;
    }
  }, [tab, filterManagement, setFiltersLoadedFromURL, filtersLoadedFromURL]);

  // Set filter ref for external access
  if (getFilterValueRef) {
    getFilterValueRef.current = () => defaultFilterValue;
  }

  // Validation checks
  const isInvalidSource = source && !isSourceEnabledForTab(source, tab, restrictSources);

  const shouldShowRestriction = shouldShowRestrictionError(
    restrictGridDisplay,
    restrictChemical,
    view,
    tab,
    isMapping,
    mappingContent
  );

  // Event handlers
  const handleClosePopup = () => {
    setShowPopup(false);
    setSkip(0);
  };

  // Render restriction error
  if (shouldShowRestriction) {
    return (
      <ErrorMessage
        title={ERROR_MESSAGES.CONTENT_NOT_AVAILABLE}
        subtitle={
          <div className="text-center">
            <button className="btn-gradient-pink" onClick={() => navigate('/plans')}>
              View our plans
            </button>
          </div>
        }
      />
    );
  }

  // Render no columns error
  if (!columns || columns.length === 0) {
    return (
      <ErrorMessage
        title={ERROR_MESSAGES.NO_COLUMNS}
        subtitle={<span>Please select columns to display in the grid.</span>}
        style={{ minHeight: 537 }}
      />
    );
  }

  // Render session expired error
  if (isSessionExpired) {
    return (
      <ErrorMessage
        title={ERROR_MESSAGES.SESSION_EXPIRED}
        subtitle={<p>Please refresh the page</p>}
      />
    );
  }

  return (
    <>
      {isInvalidSource && (
        <ErrorMessage
          title={ERROR_MESSAGES.NO_DATA_FOR_SOURCE(source, tab)}
          subtitle="Please remove or change the source filter."
        />
      )}

      {showCopiedAlert && <Alert message={alertMessage} type="success" />}

      <ActiveFiltersBar
        filters={defaultFilterValue}
        onRemoveFilter={filterManagement.handleRemoveFilter}
        onClearAll={filterManagement.handleClearAllFilters}
        isEvidence={isEvidence}
      />

      <TanStackGrid
        key={`${panelTab ?? tab}-${tab}-${view}-${source}-${isEvidence ? 'evidence' : 'summary'}`}
        columns={columnManagement.memoizedColumns}
        data={rowData}
        loading={loading}
        pagination={true}
        manualPagination={true}
        totalCount={totalCount}
        pageIndex={Math.floor(skip / limit)}
        pageSize={limit}
        pageSizeOptions={paginationAndSorting.memoizedPageSizes}
        onPageChange={paginationAndSorting.handlePageChange}
        onPageSizeChange={paginationAndSorting.handlePageSizeChange}
        manualSorting={true}
        sorting={paginationAndSorting.memoizedSorting}
        onSortingChange={paginationAndSorting.handleSortingChange}
        enableRowSelection={(isEvidence && SHOW_SUMMARIZE) || enableEnrichmentSelection}
        getRowId={enableEnrichmentSelection ? getRowId : undefined}
        rowSelection={enableEnrichmentSelection ? enrichmentRowSelection : undefined}
        onRowSelectionChange={
          enableEnrichmentSelection
            ? (updater: any) => {
                setEnrichmentRowSelection(prev => {
                  const next = typeof updater === 'function' ? updater(prev) : updater;
                  const selectedIds = Object.keys(next).filter((k: string) => next[k]);
                  enrichmentSelectedIdsRef.current = selectedIds;
                  const prevMap = enrichmentSelectedRowsMapRef.current;
                  const nextMap: Record<string, any> = {};
                  for (const id of selectedIds) {
                    const row = resolveRowForEnrichmentId(id, rowData, prevMap);
                    if (row !== undefined) nextMap[id] = row;
                  }
                  enrichmentSelectedRowsMapRef.current = nextMap;
                  const selectedRows = selectedIds.map((id: string) => nextMap[id]).filter(Boolean);
                  const cb = onDataRef.current;
                  if (cb) {
                    queueMicrotask(() =>
                      cb(selectedRows.length > 0, selectedRows, selectedIds.length, selectedIds),
                    );
                  }
                  return next;
                });
              }
            : selectionHandling.onSelectionChange
        }
        showFilters={true}
        filterValues={memoizedFilterValues}
        filterOperators={memoizedFilterOperators}
        onFilterChange={filterManagement.handleFilterChange}
        onFilterOperatorChange={filterManagement.handleFilterOperatorChange}
        columnOrder={columnOrder}
        onColumnOrderChange={setColumnOrder}
        columnVisibilityModel={columnVisibilityModel}
      />

      {showPopup && (
        <PopupMessage
          isAuthenticated={isAuthenticated}
          restrictBrowseAll={restrictBrowseAll}
          isTrial={isTrial}
          onClose={handleClosePopup}
        />
      )}

      <SaveFiltersDialog
        isOpen={showFiltersModal}
        onClose={() => setShowFiltersModal(false)}
        onConfirm={filterSaving.handleSaveFilters}
        tab={tab}
        view={view}
        source={source}
        searchItems={searchItems}
        pendingFiltersToSave={pendingFiltersToSave}
      />
    </>
  );
};

export default Grid;
